import type { Document as RichTextDocument } from '@contentful/rich-text-types';
import Grid2 from '@mui/material/Unstable_Grid2';
import type React from 'react';

import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import type { ButtonContent } from '@/components/OldButton/OldButton';
import RichText from '@/components/RichText/RichText';
import useViewPortSize from '@/hooks/useViewportSize';
import useGlobalStyles from '@/styles/global.styles';
import { MAX_IMAGE_WIDTH, setImgWidth } from '@/utils/frontify';

import useStyles from './ConfirmationStep.styles';

interface ConfirmationStepProps {
  heading: string | undefined;
  description?: RichTextDocument;
  hideImageOnMobile?: boolean;
  imageSrc: string | undefined;
  imageAltText: string | undefined;
  buttonContent?: ButtonContent;
  isOnDark: boolean;
  show: boolean;
  reverseLayout?: boolean;
  fixedGrid?: boolean;
}

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
  heading,
  description,
  hideImageOnMobile = false,
  imageSrc,
  imageAltText,
  buttonContent,
  isOnDark,
  show,
  reverseLayout,
  fixedGrid,
}) => {
  const { classes, cx } = useStyles({
    isOnDark,
    hideImageOnMobile,
  });
  const { classes: globalClasses } = useGlobalStyles({ fixed: false });
  // biome-ignore lint/complexity/useOptionalChain: Automated migration to Biome
  const button = buttonContent && buttonContent?.fields;
  const { isMobile } = useViewPortSize();
  const imageSize = isMobile ? 768 : 1440;

  return (
    <GridContainer2
      gridProps={{
        className: cx(
          show ? classes.visible : classes.hidden,
          reverseLayout ? classes.reverseLayout : classes.layout
        ),
      }}
      fixed={fixedGrid}
    >
      <Grid2
        xs={12}
        sm={9}
        md={6}
        className={cx(reverseLayout && classes.reverseContent)}
      >
        <div
          className={cx(
            fixedGrid ? globalClasses.h2 : globalClasses.h1,
            classes.heading
          )}
        >
          {heading}
        </div>
        {description && (
          <div className={globalClasses.body}>
            <RichText
              content={description}
              isOnDark={isOnDark}
              blockClasses={{ p: classes.richTextParagraph }}
            />
          </div>
        )}
        {buttonContent && (
          <Button
            type='button'
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.MD}
            label={button?.label}
            href={button?.url}
            className={classes.button}
            isOnDark={isOnDark}
          />
        )}
      </Grid2>
      <Grid2 xs={12} md={6} className={classes.hideImageOnMobile}>
        {/** biome-ignore lint/performance/noImgElement: Automated migration to Biome */}
        <img
          src={setImgWidth(imageSrc, MAX_IMAGE_WIDTH)}
          className={classes.landerImage}
          alt={imageAltText}
          width={imageSize}
        />
      </Grid2>
    </GridContainer2>
  );
};

export default ConfirmationStep;
